import dayjs from '@utils/dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import {
  CircularProgress,
  Grid,
  IconButton,
  Theme,
  Tooltip
} from '@mui/material';

import {
  MDTypography,
  DataTable,
  MDBox,
  ConfirmationModal,
  FullPageLoader,
  FaIcon,
  MDButton
} from '@components';
import MainLayout from '@layouts/MainLayout';

import { RootState } from '@store/store';
import {
  useEmptyTrashMutation,
  useLazyGetDeletedProjectsQuery,
  useRestoreProjectMutation
} from '@api/projects';
import { setUserNotifications } from '@components/Notification/slice';
import { setEmptyTrashInitiatedTime } from './slice';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';

import { sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';
import { getProjectStatus } from '@pages/MyProjects';
import { CLICKED_EMPTY_TRASH, PAGE_VISITED } from '@constants/eventNames';

const DeletedProjects: React.FC = () => {
  const reduxDispatch = useDispatch();

  const [getDeletedProjects, { data = [], isLoading }] =
    useLazyGetDeletedProjectsQuery();
  const [restoreProject] = useRestoreProjectMutation();
  const [emptyTrash, { isLoading: isDeleting, isSuccess }] =
    useEmptyTrashMutation();

  const emptyTrashInitiatedTime = useSelector(
    (state: RootState) => state?.deletedProjectsState?.emptyTrashInitiatedTime
  );

  const [isEmptyTrashModalOpen, setIsEmptyTrashModalOpen] = useState(false);

  const DeletedProjectColumns = [
    // To be implemented later
    // {
    //   id: 'selection',
    //   Header: ({ getToggleAllRowsSelectedProps }: any) => (
    //     <div>
    //       <input type='checkbox' {...getToggleAllRowsSelectedProps()} />
    //     </div>
    //   ),
    //   Cell: ({ row }: any) => (
    //     <div>
    //       <input type='checkbox' {...row.getToggleRowSelectedProps()} />
    //     </div>
    //   )
    // },
    {
      Header: '#',
      accessor: 'id',
      width: '5%',
      Cell: ({ cell }: any) => <>{Number(cell.row.id) + 1}</>
    },
    {
      Header: 'project name',
      accessor: 'project_name',
      width: '45%',
      Cell: ({ cell }: any) => {
        const typographyRef = useRef<HTMLDivElement>();

        const showTooltip =
          typographyRef?.current &&
          typographyRef?.current?.getBoundingClientRect().height > 19
            ? true
            : false;

        return (
          <Grid
            container
            textAlign='center'
            alignItems='center'
            alignContent='center'
            flexWrap='nowrap'
          >
            <Grid item>
              <FaIcon
                typographyProps={{ variant: 'lg', color: 'info' }}
                icon={
                  cell.row.original.is_api_connected
                    ? 'gear'
                    : cell.row.original.project_datafiles?.[0]?.filetype ===
                      'CSV'
                    ? 'file-csv'
                    : cell.row.original.project_datafiles?.[0]?.filetype ===
                      'GSHEET'
                    ? faGoogle
                    : 'file-excel'
                }
              />
            </Grid>
            <Grid item>
              <MDBox
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  ml: 1
                }}
              >
                <Tooltip
                  title={showTooltip ? cell.row.values.project_name || '' : ''}
                  placement='top'
                >
                  <MDTypography
                    ref={typographyRef}
                    variant={'sm'}
                    color={'info'}
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      wordBreak: 'break-all'
                    }}
                  >
                    {cell.row.values.project_name}
                  </MDTypography>
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        );
      }
    },
    {
      Header: () => (
        <Tooltip
          title={
            <div style={{ textAlign: 'left' }}>
              An uploaded file can have following status changes:
              <br />
              1. “Processing” when data is being read and recognised
              <br />
              2. “Processed” when data is successfully recognised and ready for
              next step i.e. Review
              <br />
              3. “Cleaning” when DataSquirrel is cleaning the imported data
              <br />
              4. “Ready” when data is cleaned, and a Project is created
            </div>
          }
          placement='right'
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 2,
              width: 'min-content'
            }}
          >
            <span>Status</span>
            <FaIcon
              icon='circle-question'
              style={{
                verticalAlign: '0.125rem'
              }}
              typographyProps={{
                lineHeight: 0.625,
                color: 'info'
              }}
            />
          </div>
        </Tooltip>
      ),
      accessor: 'project_status',
      width: '25%',
      Cell: ({ cell }: any) => (
        <Grid container direction='row' spacing={1} alignItems='center'>
          <Grid item>
            <MDBox
              color={getProjectStatus(cell.row.values.project_status)?.color}
            >
              <FontAwesomeIcon icon='circle' />
            </MDBox>
          </Grid>
          <Grid item>
            <Tooltip
              title={
                getProjectStatus(cell.row.values.project_status)?.tooltipText ||
                ''
              }
              placement='top'
            >
              <span>
                {getProjectStatus(cell.row.values.project_status)?.status || (
                  <></>
                )}
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      )
    },
    {
      Header: '# of charts',
      accessor: 'chart_id_order',
      width: '5%',
      Cell: ({ cell }: any) => cell.row.values.chart_id_order?.length || 0
    },
    {
      Header: 'last updated',
      accessor: 'updated_at',
      width: '15%',
      Cell: ({ cell }: any) => dayjs(cell.row.values.updated_at).fromNow()
    },

    {
      Header: '',
      width: '5%',
      Cell: ({ cell }: any) => (
        <IconButton
          size='small'
          onClick={() => handleRestoreButtonClick(cell.row.original.id)}
          sx={({ palette }: Theme) => ({
            '&:hover': {
              color: palette.dark.main
            }
          })}
        >
          <FaIcon
            icon='undo'
            typographyProps={{ color: 'inherit', variant: 'xl' }}
          />
        </IconButton>
      ),
      accessor: 'hoverItem'
    }
  ];

  useEffect(() => {
    sendAmplitudeData(PAGE_VISITED);
    sendGAData(PAGE_VISITED);
    const timeDiff = emptyTrashInitiatedTime
      ? dayjs().diff(dayjs(emptyTrashInitiatedTime), 'second')
      : 0;

    setTimeout(
      () => {
        getDeletedProjects();
        reduxDispatch(setEmptyTrashInitiatedTime(''));
      },
      timeDiff > 8 ? 0 : timeDiff * 1000
    );
  }, []);

  const handleRestoreButtonClick = (id: string) => {
    restoreProject(id);
  };

  const handleEmptyTrashClick = async () => {
    reduxDispatch(setEmptyTrashInitiatedTime(dayjs().format()));
    emptyTrash({});
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: false,
      is_user_message: true,
      message_event: MSG_EVENT.EMPTY_TRASH,
      message_text: 'Emptying trash in progress',
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.SUCCESS,
      message_data: JSON.stringify({ project: { id: '', name: '' } }),
      is_important: false,
      recipient_obj: 0
    };

    reduxDispatch(setUserNotifications(payload));

    setIsEmptyTrashModalOpen(false);
  };

  if (!data && (emptyTrashInitiatedTime || isLoading))
    return <FullPageLoader />;

  return (
    <>
      <MainLayout pageTitle='Deleted Projects'>
        <Grid container mt='1rem' justifyContent='space-between'>
          <Grid item>
            <MDTypography variant='h3' textGradient color={'info'}>
              Deleted Projects
            </MDTypography>
          </Grid>
          {data?.length > 0 && !isDeleting && !isSuccess && (
            <Grid item>
              <MDButton
                color='light'
                size='medium'
                onClick={() => {
                  setIsEmptyTrashModalOpen(true);
                  sendAmplitudeData(CLICKED_EMPTY_TRASH);
                }}
                sx={{ lineHeight: '0.5rem' }}
              >
                <FaIcon
                  typographyProps={{ variant: 'xs', color: 'primary' }}
                  icon='trash-can'
                />
                <MDTypography pl={1} variant='xs' color='info'>
                  EMPTY TRASH
                </MDTypography>
              </MDButton>
            </Grid>
          )}
        </Grid>
        <Grid container mt={2}>
          {isDeleting && !isSuccess ? (
            <MDBox
              display='flex'
              flex={1}
              position='absolute'
              justifyContent='center'
              top='50%'
              left='50%'
            >
              <CircularProgress color='secondary' />
            </MDBox>
          ) : (
            <DataTable
              table={{
                columns: DeletedProjectColumns,
                rows: isSuccess ? [] : data || []
              }}
              showTotalEntries={false}
              entriesPerPage={false}
            />
          )}
        </Grid>
      </MainLayout>
      {isEmptyTrashModalOpen && (
        <ConfirmationModal
          open={isEmptyTrashModalOpen}
          onClose={() => setIsEmptyTrashModalOpen(false)}
          onProceedButtonClick={handleEmptyTrashClick}
          title='Empty Trash ?'
          subtitle={
            data?.length > 1
              ? `${data?.length} Projects will be permanently deleted from your account. 
              Are you sure you want to go ahead with it ?`
              : `${data?.length} Project will be permanently deleted from your account. 
              Are you sure you want to go ahead with it ?`
          }
          showSubTitle={true}
          proceedButtonMessage='YES, DELETE'
          cancelButtonText='CANCEL'
          isEmptyTrash
        />
      )}
    </>
  );
};

export default DeletedProjects;
